import { useEffect, useRef, useState } from 'react';

// 📦 Estoque do GIX dentro do card (14/10/2026, Compras Revenda do Evandro).
// Aparece nas etapas com painelEstoqueGix: o comprador procura o produto e vê,
// por filial, saldo, disponível, vendas, cobertura e o que já está pedido em OC.
// Os produtos escolhidos ficam guardados no próprio card (campo oculto) para quem
// abrir depois, como o gestor na aprovação, ver o mesmo panorama.

interface Produto { prodcodi: number; descricao: string; unidade: string;
  total: { saldo: number; disponivel: number; vendas: number; oc_pendente: number; cobertura_dias: number | null; media_mes: number };
  empresas: { empresa: number; cidade: string; saldo: number; disponivel: number; vendas: number; oc_pendente: number; cobertura_dias: number | null; ocs: { oc: number; data: string; qtd: number }[] }[]; }

interface Props { codigosIniciais: number[]; onMudarCodigos: (codigos: number[]) => void; }

const n = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
const cob = (d: number | null) => d == null ? '—' : d > 999 ? '999+ d' : `${Math.round(d)} d`;
const corCob = (d: number | null) => d == null ? 'text-gray-400' : d < 15 ? 'text-red-600 font-semibold' : d < 30 ? 'text-amber-600 font-semibold' : d > 120 ? 'text-gray-500' : 'text-gray-900';

export default function PainelEstoqueGix({ codigosIniciais, onMudarCodigos }: Props) {
  const [codigos, setCodigos] = useState<number[]>(codigosIniciais);
  const [dias, setDias] = useState(90);
  const [dados, setDados] = useState<Produto[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [sugestoes, setSugestoes] = useState<{ codigo: number; descricao: string }[]>([]);
  const [aberto, setAberto] = useState<number | null>(null);
  const timer = useRef<any>(null);

  useEffect(() => {
    if (!codigos.length) { setDados([]); return; }
    setCarregando(true); setErro('');
    fetch(`/api/gix/produto-panorama?codigos=${codigos.join(',')}&dias=${dias}`)
      .then(r => r.json()).then(d => { if (d.status === 'ok') setDados(d.produtos); else setErro(d.message || 'Erro ao consultar o GIX'); })
      .catch(() => setErro('Sem conexão com o GIX'))
      .finally(() => setCarregando(false));
  }, [codigos, dias]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (busca.trim().length < 3) { setSugestoes([]); return; }
    timer.current = setTimeout(() => {
      fetch(`/api/gix/produtos?q=${encodeURIComponent(busca.trim())}&limit=12`).then(r => r.json())
        .then(d => setSugestoes((d.results || d.dados || d.data || []).slice?.(0, 12).map((x: any) => ({
          codigo: Number(x.codigo ?? x.prodcodi ?? x.id), descricao: String(x.descricao ?? x.nome ?? x.proddesc ?? '') })) || []))
        .catch(() => setSugestoes([]));
    }, 350);
  }, [busca]);

  const mudar = (novos: number[]) => { setCodigos(novos); onMudarCodigos(novos); };
  const adiciona = (c: number) => { if (!codigos.includes(c)) mudar([...codigos, c].slice(0, 20)); setBusca(''); setSugestoes([]); };

  return (
    <div className="border border-gray-200/80 rounded-xl overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5">
      <div className="flex items-center gap-2.5 px-3 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center"><span className="text-white text-xs">📦</span></div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-sm tracking-tight">Estoque no GIX</h3>
          <span className="text-[10px] text-gray-500 font-medium">saldo, vendas, cobertura e OC pendente por filial</span>
        </div>
        <select value={dias} onChange={e => setDias(Number(e.target.value))} className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white" title="Período das vendas">
          {[30, 60, 90, 180].map(d => <option key={d} value={d}>vendas {d} dias</option>)}
        </select>
      </div>
      <div className="p-3 space-y-3">
        <div className="relative">
          <input value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && /^\d+$/.test(busca.trim())) adiciona(Number(busca.trim())); }}
            placeholder="Procurar produto no GIX (código ou descrição)…" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2" />
          {sugestoes.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {sugestoes.map(s => <button key={s.codigo} onClick={() => adiciona(s.codigo)} className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50">
                <span className="text-gray-500 mr-2">{s.codigo}</span>{s.descricao}</button>)}
            </div>)}
        </div>
        {erro && <div className="text-xs text-red-600">{erro}</div>}
        {carregando && <div className="text-xs text-gray-500">Consultando o GIX…</div>}
        {!codigos.length && <div className="text-xs text-gray-500">Procure os produtos da demanda para ver o estoque das filiais.</div>}
        {dados.map(p => (
          <div key={p.prodcodi} className="border border-gray-100 rounded-lg">
            <div className="flex items-start gap-2 px-3 py-2">
              <button onClick={() => setAberto(aberto === p.prodcodi ? null : p.prodcodi)} className="flex-1 text-left">
                <div className="text-[13px] font-semibold text-gray-900 leading-snug">{p.prodcodi} · {p.descricao}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[12px] text-gray-600">
                  <span>Saldo <b className="text-gray-900">{n(p.total.saldo)}</b></span>
                  <span>Disponível <b className="text-gray-900">{n(p.total.disponivel)}</b></span>
                  <span>Média/mês <b className="text-gray-900">{n(p.total.media_mes)}</b></span>
                  <span>Cobertura <b className={corCob(p.total.cobertura_dias)}>{cob(p.total.cobertura_dias)}</b></span>
                  <span>Em OC <b className={p.total.oc_pendente ? 'text-gray-900' : 'text-gray-400'}>{n(p.total.oc_pendente)}</b></span>
                </div>
              </button>
              <button onClick={() => mudar(codigos.filter(c => c !== p.prodcodi))} className="text-gray-400 hover:text-red-600 text-sm" title="Tirar do painel">✕</button>
            </div>
            {aberto === p.prodcodi && (
              <table className="w-full text-[12px] border-t border-gray-100">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 bg-gray-50">
                  <th className="px-3 py-1.5">Filial</th><th className="px-2 py-1.5 text-right">Saldo</th><th className="px-2 py-1.5 text-right">Disp.</th>
                  <th className="px-2 py-1.5 text-right">Vendas</th><th className="px-2 py-1.5 text-right">Cobertura</th><th className="px-3 py-1.5 text-right">Em OC</th></tr></thead>
                <tbody>{p.empresas.map(e => (
                  <tr key={e.empresa} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 text-gray-900">{String(e.empresa).padStart(2, '0')} · {e.cidade}</td>
                    <td className="px-2 py-1.5 text-right">{n(e.saldo)}</td><td className="px-2 py-1.5 text-right">{n(e.disponivel)}</td>
                    <td className="px-2 py-1.5 text-right">{n(e.vendas)}</td><td className={`px-2 py-1.5 text-right ${corCob(e.cobertura_dias)}`}>{cob(e.cobertura_dias)}</td>
                    <td className="px-3 py-1.5 text-right" title={e.ocs.map(o => `OC ${o.oc} · ${o.data?.split('-').reverse().join('/')} · ${n(o.qtd)}`).join('\n')}>{e.oc_pendente ? n(e.oc_pendente) : '—'}</td>
                  </tr>))}</tbody>
              </table>)}
          </div>
        ))}
      </div>
    </div>
  );
}
